import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { ChatInterface } from "@/components/chat/ChatInterface";
import { RegisterFlow } from "@/components/auth/RegisterFlow";
import { Logo } from "@/components/layout/Logo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ToolIntro } from "@/components/chat/ToolIntro";
import { ConversationRail } from "@/components/chat/ConversationRail";
import { Plus, GraduationCap, Rocket, CheckCircle2 } from "lucide-react";

// `agent` = null (advisor) or "cover_letter" | "orientation" | "predictor".
// Authenticated users get the full app layout + history rail. Guests who open a
// tool directly get a lightweight chat limited to 5 messages, with the student
// level read from the URL (?level=postgraduate|undergraduate) to pick the agent.
export function AgentChat({ agent = null }) {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [reloadKey, setReloadKey] = useState(0);
  const [active, setActive] = useState(null);
  const [chatKey, setChatKey] = useState("new");
  const [showSignup, setShowSignup] = useState(false);
  const listParam = agent || "advisor";

  // Decide the chrome once (guests keep the light chrome even after they sign up
  // in-place, so the live conversation is never remounted / lost).
  const guestModeRef = useRef(null);
  if (user !== null && guestModeRef.current === null) guestModeRef.current = !user;
  const guestMode = guestModeRef.current;

  const urlPersona = params.get("level") === "postgraduate" ? "postgraduate" : "undergraduate";
  const persona = user?.persona || urlPersona;
  const utmSource = params.get("utm_source") || undefined;
  const focusId = user && !agent ? params.get("focus") : null;
  const [focus, setFocus] = useState(null);
  useEffect(() => {
    if (!focusId) { setFocus(null); return; }
    api.get(`/catalog/item/${encodeURIComponent(focusId)}`)
      .then((r) => { setFocus(r.data); setActive(null); setChatKey("focus-" + focusId); })
      .catch(() => setFocus(null));
  }, [focusId]);

  const load = useCallback(() => setReloadKey((k) => k + 1), []);

  const openConv = async (id) => {
    const { data } = await api.get(`/chat/conversations/${id}`);
    setActive(data);
    setChatKey(id);
  };
  const newChat = () => { setActive(null); setChatKey("new-" + Date.now()); };
  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-xl bg-orange animate-pulse" /></div>;
  }

  const chat = (
    <ChatInterface
      key={chatKey}
      bare
      agent={agent || undefined}
      persona={persona}
      isGuest={!user}
      utmSource={utmSource}
      focus={chatKey.startsWith("focus-") ? focus : null}
      initialMessages={active?.messages || []}
      conversationId={active?.id || null}
      onFirstMessage={() => user && setTimeout(load, 1500)}
      onLimitReached={() => setShowSignup(true)}
    />
  );

  const signupDialog = (
    <Dialog open={showSignup} onOpenChange={setShowSignup}>
      <DialogContent className="max-w-md sm:max-w-lg max-h-[92vh] overflow-y-auto p-6" data-testid="guest-signup-modal">
        <div className="mb-2 flex items-center gap-2 text-sm text-orange font-semibold">
          <CheckCircle2 className="h-4 w-4" /> You've used your 5 free messages
        </div>
        <p className="text-sm text-muted-foreground mb-4">Create your free Elite account to keep chatting — your current conversation will be saved and continue right here.</p>
        <RegisterFlow compact onSuccess={() => setShowSignup(false)} onLoginClick={() => { window.location.href = "/login"; }} />
      </DialogContent>
    </Dialog>
  );

  // ---- Guest (light) chrome ----
  if (guestMode) {
    return (
      <div className="flex flex-col h-screen bg-background" data-testid="guest-agent">
        <header className="flex items-center justify-between px-4 sm:px-6 h-16 border-b border-border bg-white shrink-0">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-navy bg-navy/5 rounded-full px-3 py-1.5">
              {persona === "postgraduate" ? <Rocket className="h-3.5 w-3.5 text-orange" /> : <GraduationCap className="h-3.5 w-3.5 text-orange" />}
              {persona === "postgraduate" ? "Postgraduate" : "Undergraduate"}
            </span>
            {user ? (
              <Button asChild size="sm" className="rounded-full bg-orange hover:bg-orange-dark text-white"><Link to="/dashboard" data-testid="guest-to-dashboard">My dashboard</Link></Button>
            ) : (
              <>
                <Link to="/login" className="text-sm font-semibold text-navy hover:text-orange" data-testid="guest-login-link">Log in</Link>
                <Button size="sm" className="rounded-full bg-orange hover:bg-orange-dark text-white" data-testid="guest-signup-btn" onClick={() => setShowSignup(true)}>Sign up free</Button>
              </>
            )}
          </div>
        </header>
        {user && (
          <div className="bg-green-50 text-green-700 text-sm text-center py-2 px-4" data-testid="guest-signed-in-banner">
            Account created — your conversation is saved. Keep chatting or open your dashboard.
          </div>
        )}
        <ToolIntro textKey={agent || "advisor"} persona={persona} />
        <div className="flex-1 min-h-0">{chat}</div>
        {signupDialog}
      </div>
    );
  }

  // ---- Authenticated (full) chrome ----
  return (
    <AppLayout>
      <div className="flex h-[calc(100dvh-8rem-var(--banner-h,0px))] lg:h-[calc(100vh-var(--banner-h,0px))]">
        <aside className="hidden md:flex flex-col w-72 shrink-0 border-r border-border bg-white">
          <div className="p-3 border-b border-border">
            <Button data-testid="new-chat-btn" onClick={newChat} className="w-full rounded-full bg-orange hover:bg-orange-dark text-white font-display font-bold italic uppercase text-xs tracking-wide">
              <Plus className="h-4 w-4 mr-1" /> New chat
            </Button>
          </div>
          <ConversationRail agentParam={listParam} activeId={active?.id} reloadKey={reloadKey} onOpen={openConv} onActiveDeleted={newChat} />
        </aside>
        <div className="flex-1 min-w-0 flex flex-col">
          <ToolIntro textKey={agent || "advisor"} persona={user?.persona || persona} />
          <div className="flex-1 min-h-0">{chat}</div>
        </div>
      </div>
    </AppLayout>
  );
}
